import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Spinner } from "@heroui/react";
import { Link } from "react-router-dom";
import { PencilIcon } from "@heroicons/react/24/solid";
import { K1DataTable } from "../components/K1DataTable";
import K1FilterBar from "../components/K1FilterBar";
import { useK1DashboardContext } from "../context/K1DashboardProvider";
import {
  buildFundScopeParams,
  buildK1DownloadUrl,
  fundSelectionKey,
  k1Api,
  type K1FundSelection,
} from "../services/api";
import { DownloadButton } from "@/components/DownloadButton";

function buildDownloadUrl(selection: K1FundSelection): string {
  const params = buildFundScopeParams(selection);
  return buildK1DownloadUrl("k1_management_companies", params);
}

type Row = Record<string, unknown>;

const columns: ColumnDef<Row>[] = [
  {
    accessorKey: "name",
    header: "Name",
    size: 220,
    cell: ({ getValue, row }) => {
      const v = getValue();
      return (
        <div className="flex items-center gap-1.5">
          <span className="font-medium truncate">
            {v !== null && v !== undefined ? String(v) : ""}
          </span>
          <Link
            to={String(row.original.id)}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 text-gray-400 hover:text-gray-700"
            title="Edit"
            aria-label="Edit"
          >
            <PencilIcon className="h-3.5 w-3.5" />
          </Link>
        </div>
      );
    },
  },
  {
    accessorKey: "fund_names",
    header: "Managed Funds",
    size: 380,
    cell: ({ getValue }) => {
      const v = getValue();
      if (!v) return <span className="text-slate-300 text-xs">—</span>;
      const funds = String(v).split(", ");
      return (
        <div className="flex flex-wrap gap-1 py-0.5">
          {funds.map((name, i) => (
            <span
              key={i}
              className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600"
            >
              {name}
            </span>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: "country",
    header: "Country",
    size: 110,
    cell: ({ getValue }) => {
      const v = getValue();
      return v !== null && v !== undefined ? String(v) : "";
    },
  },
  {
    accessorKey: "address",
    header: "Address",
    size: 220,
    cell: ({ getValue }) => {
      const v = getValue();
      return v !== null && v !== undefined ? String(v) : "";
    },
  },
  {
    accessorKey: "contacts",
    header: "Contacts",
    size: 280,
    cell: ({ getValue }) => {
      const v = getValue();
      if (!Array.isArray(v) || v.length === 0) return "";
      const emails = (v as unknown[]).flatMap((c) => {
        if (typeof c === "string") return c ? [c] : [];
        if (
          c &&
          typeof c === "object" &&
          "email" in c &&
          typeof (c as { email?: unknown }).email === "string"
        )
          return [(c as { email: string }).email];
        return [];
      });
      if (emails.length === 0) return "";
      return (
        <div className="flex flex-col gap-0.5 py-0.5">
          {emails.map((email, i) => (
            <a
              key={i}
              href={`mailto:${email}`}
              className="truncate text-[12px] text-blue-600 hover:text-blue-800 hover:underline"
              title={email}
            >
              {email}
            </a>
          ))}
        </div>
      );
    },
  },
];

export default function ManagementCompaniesView() {
  const { selectedFundIds, fundSelection } = useK1DashboardContext();

  const scopeKey = fundSelectionKey(fundSelection);

  const { data, isLoading } = useQuery({
    queryKey: ["k1", "management-companies", scopeKey],
    queryFn: () => k1Api.getManagementCompanies(fundSelection),
    enabled: selectedFundIds.length > 0,
    staleTime: 60_000,
  });

  const rows = useMemo(() => (data?.data ?? []) as Row[], [data]);

  return (
    <div className="flex flex-col gap-4">
      <K1FilterBar hidePeriod />
      {selectedFundIds.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      ) : isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-1">
            <h1 className="font-semibold text-xl text-primary">
              General Partners
            </h1>
            <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
              {rows.length.toLocaleString()} companies
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
            <K1DataTable<Row, unknown>
              table_name="k1-management-companies"
              columns={columns}
              data={rows}
              getRowClassName={() => "hover:!bg-slate-50"}
              pinnedLeft={["name"]}
              noCard
            />
            <div className="flex items-center border-t border-slate-200 px-4 py-3">
              <DownloadButton
                href={buildDownloadUrl(fundSelection)}
                filename={`k1_management_companies_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`}
                size="sm"
                text="Download"
                className="text-sm"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
